import { Component, effect, inject, input, signal } from '@angular/core';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
  IonContent,
  IonFooter,
  ModalController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { closeOutline, checkmarkOutline, diceOutline } from 'ionicons/icons';
import {
  SuggestedMessageChoice,
  SuggestedMessageEntry,
} from 'src/app/features/chat/types/extra-data';
import {
  formatSuggestedMessagePreview,
  formatSuggestedMessageRollDescriptions,
  messageHasRollMarkers,
} from 'src/app/features/chat/utils/suggested-message-compose';
import { Character } from 'src/app/features/characters/types/character';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';

export type SuggestedMessageModalResult = {
  characterId: number;
  choiceIndex: number;
  choice: SuggestedMessageChoice;
};

@Component({
  selector: 're-suggested-messages-modal',
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonIcon,
    IonContent,
    IonFooter,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './suggested-messages-modal.component.html',
  styleUrls: ['./suggested-messages-modal.component.scss'],
})
export class SuggestedMessagesModalComponent {
  entries = input<SuggestedMessageEntry[]>([]);
  characters = input<Character[]>([]);
  initialSelection = input<SuggestedMessageModalResult | null>(null);

  private modalController = inject(ModalController);

  selected = signal<SuggestedMessageModalResult | null>(null);

  constructor() {
    addIcons({ closeOutline, checkmarkOutline, diceOutline });
    effect(() => {
      const initial = this.initialSelection();
      if (initial) this.selected.set(initial);
    });
  }

  isSelected(characterId: number, choiceIndex: number): boolean {
    const s = this.selected();
    return s !== null && s.characterId === characterId && s.choiceIndex === choiceIndex;
  }

  select(characterId: number, choiceIndex: number, choice: SuggestedMessageChoice): void {
    this.selected.set({ characterId, choiceIndex, choice });
  }

  characterName(characterId: number): string {
    return this.characters().find((c) => c.id === characterId)?.name ?? '';
  }

  choiceLabel(choice: SuggestedMessageChoice): string {
    if (choice.label) return choice.label;
    const preview = this.choicePreview(choice);
    return preview.length > 60 ? preview.slice(0, 60) + '…' : preview;
  }

  choicePreview(choice: SuggestedMessageChoice): string {
    if (!messageHasRollMarkers(choice.message)) return choice.message;
    const ops = choice.choiceRoll ? [choice.choiceRoll] : [{ name: 'Tiro' }];
    return formatSuggestedMessagePreview(choice.message, ops);
  }

  choiceRollDescription(choice: SuggestedMessageChoice): string | null {
    const roll = choice.choiceRoll;
    if (!roll) return null;
    return formatSuggestedMessageRollDescriptions([roll]);
  }

  choiceHasRoll(choice: SuggestedMessageChoice): boolean {
    return choice.choiceRoll != null || messageHasRollMarkers(choice.message);
  }

  confirm(): void {
    const s = this.selected();
    if (!s) return;
    this.modalController.dismiss(s);
  }

  dismiss(): void {
    this.modalController.dismiss(null);
  }
}
